import type { Session } from "@supabase/supabase-js";
import {
  AlertCircle,
  ArrowLeft,
  Bell,
  BellOff,
  CalendarCheck,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  CircleUserRound,
  ExternalLink,
  FileImage,
  Inbox,
  Link2,
  LoaderCircle,
  LogIn,
  LogOut,
  RefreshCw,
  Save,
  Settings,
  ShieldCheck,
  Trash2,
  Upload,
  Video,
  Zap,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { viewUrl } from "../App";
import {
  ADMIN_NOTIFICATION_CHANNEL,
  ADMIN_NOTIFICATION_PREFERENCE_KEY,
  adminNotificationCopy,
  adminNotificationUrl,
  adminTabFromSearch,
  parseAdminBroadcast,
  type AdminTab,
} from "../lib/adminNotifications";
import { api } from "../lib/api";
import { appConfig, supabase } from "../lib/config";
import { formatAdminDateTime } from "../lib/date";
import { extractYouTubeId } from "../lib/youtube";
import type {
  AdminOverview,
  AvailabilityWindow,
  BookingRequestRecord,
  ChallengeRecord,
} from "../types";

type RealtimeNotificationStatus = "disabled" | "connecting" | "connected" | "error";

const ADMIN_NOTIFICATION_SERVICE_WORKER = "/admin-notifications-sw.js?v=3";
const weekdays = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

function LoginPanel() {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  function adminRedirectUrl() {
    const redirectUrl = new URL(window.location.href);
    redirectUrl.search = "?view=admin";
    redirectUrl.hash = "";
    return redirectUrl.toString();
  }

  async function loginWithGoogle() {
    if (!supabase) return setError("Supabase ist noch nicht konfiguriert.");
    setLoading(true);
    setError("");
    const { error: authError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: adminRedirectUrl(),
        scopes: "openid email profile",
      },
    });
    if (authError) {
      setError(authError.message);
      setLoading(false);
    }
  }

  return (
    <main className="admin-login">
      <a className="back-link" href={viewUrl("landing")}><ArrowLeft /> Zur Landingpage</a>
      <div className="login-card">
        <span className="login-icon"><ShieldCheck /></span>
        <h1>Geschützter Bereich</h1>
        <p>Nur die hinterlegte Administrator-Adresse erhält Zugang.</p>
        {!appConfig.isConfigured && <div className="alert alert-warning"><AlertCircle /> Trage zuerst die Supabase-Werte in der Umgebungskonfiguration ein.</div>}
        {appConfig.stagingMode && <div className="alert alert-warning"><AlertCircle /> Testumgebung: Änderungen bleiben vollständig in Staging.</div>}
        {error && <div className="alert alert-error"><AlertCircle /> {error}</div>}
        <button className="button button-google full-width" type="button" onClick={() => void loginWithGoogle()} disabled={loading || !appConfig.isConfigured}>
          {loading ? <><LoaderCircle className="spin" /> Weiter zu Google …</> : <><LogIn /> Mit Google anmelden</>}
        </button>
      </div>
    </main>
  );
}

function StatusBadge({ status }: { status: string }) {
  const labels: Record<string, string> = {
    pending: "Offen",
    approved: "Bestätigt",
    rejected: "Abgelehnt",
    slot_taken: "Alternative angeboten",
    expired: "Abgelaufen",
    calendar_removed: "Kalendereintrag fehlt",
    cancellation_pending: "Stornierung läuft",
    cancelled: "Storniert",
    cancellation_failed: "Stornierung fehlgeschlagen",
    upload_pending: "Upload läuft",
    new: "Neu",
    in_progress: "In Bearbeitung",
    video_ready: "Video fertig",
    published: "Veröffentlicht",
    archived: "Archiviert",
  };
  return <span className={`status-badge status-${status}`}>{labels[status] ?? status}</span>;
}

function BookingAdminCard({ booking, busy, onAction }: {
  booking: BookingRequestRecord;
  busy: boolean;
  onAction: (action: string, data: Record<string, unknown>) => Promise<void>;
}) {
  return (
    <article className="admin-item">
      <div className="admin-item-head">
        <div><strong>{formatAdminDateTime(booking.slotStart)}</strong><small>Angefragt {formatAdminDateTime(booking.createdAt)}</small></div>
        <StatusBadge status={booking.status} />
      </div>
      <div className="admin-details-grid">
        <span><small>Name</small><strong>{booking.name}</strong></span>
        <span><small>Kontakt</small><strong>{booking.email}</strong>{booking.phone && <small>{booking.phone}</small>}</span>
        <span><small>Anfrage als</small><strong>{booking.role === "parent" ? "Elternteil" : "Schüler/in"}</strong>{booking.studentAge !== undefined && <small>{booking.studentAge} Jahre</small>}</span>
        <span><small>Schule</small><strong>{booking.grade}</strong><small>{booking.schoolType}</small></span>
        {booking.guardianName && <span><small>Elternkontakt</small><strong>{booking.guardianName}</strong><small>{booking.guardianEmail}</small><small>{booking.guardianPhone}</small></span>}
        <span className="wide"><small>Thema / Ziel</small><p>{booking.topicGoal}</p></span>
        {booking.attachment?.fileUrl && <span className="wide booking-attachment-admin"><small>Aktuelle Aufgabe</small><a className="file-link" href={booking.attachment.fileUrl} target="_blank" rel="noreferrer"><FileImage /> {booking.attachment.originalFilename} <ExternalLink /></a></span>}
        {booking.cancellationReason && <span className="wide"><small>Stornierungsgrund</small><p>{booking.cancellationReason}</p></span>}
      </div>
      {booking.status === "pending" && (
        <div className="admin-actions">
          <button className="button button-small button-success" type="button" disabled={busy} onClick={() => onAction("approveBooking", { bookingId: booking.id })}><CalendarCheck /> Bestätigen</button>
          <button className="button button-small button-secondary" type="button" disabled={busy} onClick={() => onAction("rejectBooking", { bookingId: booking.id, offerAlternatives: true })}><RefreshCw /> Alternativen</button>
          <button className="button button-small button-danger" type="button" disabled={busy} onClick={() => onAction("rejectBooking", { bookingId: booking.id, offerAlternatives: false })}><XCircle /> Ablehnen</button>
        </div>
      )}
      {["approved", "cancellation_pending", "cancellation_failed"].includes(booking.status) && (
        <div className="admin-actions">
          <button className="button button-small button-danger" type="button" disabled={busy} onClick={async () => {
            if (!window.confirm("Diesen bestätigten Termin wirklich absagen? Der Google-Termin und Meet-Link werden entfernt und der Gast benachrichtigt.")) return;
            const reason = window.prompt("Grund für die Absage (optional):", booking.cancellationReason ?? "");
            if (reason === null) return;
            await onAction("cancelBooking", { bookingId: booking.id, reason: reason.trim() });
          }}><XCircle /> {booking.status === "approved" ? "Termin absagen" : "Stornierung erneut versuchen"}</button>
        </div>
      )}
    </article>
  );
}

function ChallengeAdminCard({ challenge, session, onSaved }: {
  challenge: ChallengeRecord;
  session: Session;
  onSaved: (message: string) => Promise<void>;
}) {
  const [youtubeUrl, setYoutubeUrl] = useState(challenge.youtubeUrl ?? "");
  const [title, setTitle] = useState(challenge.publicTitle ?? "");
  const [teaser, setTeaser] = useState(challenge.publicTeaser ?? "");
  const [status, setStatus] = useState(challenge.status);
  const [previewPath, setPreviewPath] = useState(challenge.publicPreviewPath ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function save(publish = false) {
    const videoId = youtubeUrl ? extractYouTubeId(youtubeUrl) : null;
    if (youtubeUrl && !videoId) return setError("Bitte trage einen gültigen YouTube-Link ein.");
    if (publish && (!videoId || !title.trim() || !teaser.trim())) return setError("Zum Veröffentlichen fehlen Video-Link, Titel oder Teaser.");
    if (publish && !challenge.publicationConsent) return setError("Diese Einsendung hat keine Freigabe zur Veröffentlichung.");
    setBusy(true);
    setError("");
    try {
      await api.adminAction(session, publish ? "publishChallenge" : "saveChallenge", {
        challengeId: challenge.id,
        youtubeUrl,
        youtubeVideoId: videoId,
        publicTitle: title,
        publicTeaser: teaser,
        publicPreviewPath: previewPath || null,
        status: publish ? "published" : status,
      });
      await onSaved(publish ? "Challenge wurde veröffentlicht." : "Challenge wurde gespeichert.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Speichern fehlgeschlagen.");
    } finally { setBusy(false); }
  }

  async function uploadPreview(file?: File) {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 5 * 1024 * 1024) {
      setError("Die öffentliche Vorschau muss JPG, PNG oder WebP und maximal 5 MB groß sein.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const prepared = await api.adminAction<{ path: string; token: string }>(session, "preparePublicPreview", { challengeId: challenge.id, file: { name: file.name, type: file.type, size: file.size } });
      await api.uploadPublicPreview(prepared.path, prepared.token, file);
      setPreviewPath(prepared.path);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Upload fehlgeschlagen."); }
    finally { setBusy(false); }
  }

  return (
    <article className="admin-item challenge-admin-item">
      <div className="admin-item-head"><div><strong>{challenge.code} · {challenge.topic}</strong><small>{challenge.firstName} · {challenge.grade} · {formatAdminDateTime(challenge.createdAt)}</small></div><StatusBadge status={challenge.status} /></div>
      <div className="admin-details-grid">
        <span><small>Kontakt</small><strong>{challenge.email || "anonym"}</strong>{challenge.phone && <small>{challenge.phone}</small>}</span>
        <span><small>Veröffentlichung</small><strong>{challenge.publicationConsent ? "Freigegeben" : "Nicht freigegeben"}</strong><small>{challenge.notificationOptIn ? "Benachrichtigung gewünscht" : "Keine Benachrichtigung"}</small></span>
        <span className="wide"><small>Frage</small><p>{challenge.description}</p></span>
      </div>
      {challenge.privateFileUrl && <a className="file-link" href={challenge.privateFileUrl} target="_blank" rel="noreferrer"><FileImage /> Privaten Upload ansehen <ExternalLink /></a>}
      <div className="admin-edit-grid">
        <label>Status<select value={status} onChange={(event) => setStatus(event.target.value as ChallengeRecord["status"])}><option value="new">Neu</option><option value="in_progress">In Bearbeitung</option><option value="video_ready">Video fertig</option><option value="archived">Archiviert</option></select></label>
        <label>YouTube-Link<input value={youtubeUrl} onChange={(event) => setYoutubeUrl(event.target.value)} placeholder="https://youtu.be/…" /></label>
        <label>Öffentlicher Titel<input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={120} /></label>
        <label>Kurzer Teaser<input value={teaser} onChange={(event) => setTeaser(event.target.value)} maxLength={280} /></label>
        <label className="preview-upload"><span>Bereinigte Vorschau <em>optional</em></span><input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => void uploadPreview(event.target.files?.[0])} /><i><Upload /> {previewPath ? "Vorschau ersetzen" : "Vorschau hochladen"}</i></label>
      </div>
      {error && <div className="alert alert-error"><AlertCircle /> {error}</div>}
      <div className="admin-actions">
        <button className="button button-small button-secondary" type="button" disabled={busy} onClick={() => void save(false)}><Save /> Speichern</button>
        <button className="button button-small button-success" type="button" disabled={busy} onClick={() => void save(true)}><Video /> {challenge.status === "published" ? "Veröffentlichung aktualisieren" : "Veröffentlichen"}</button>
        <button className="button button-small button-danger" type="button" disabled={busy} onClick={async () => { if (!window.confirm("Challenge und privaten Upload wirklich löschen?")) return; setBusy(true); await api.adminAction(session, "deleteChallenge", { challengeId: challenge.id }); await onSaved("Challenge wurde gelöscht."); }}><Trash2 /> Löschen</button>
      </div>
    </article>
  );
}

function ScheduleEditor({ schedule, session, onSaved }: { schedule: AvailabilityWindow[]; session: Session; onSaved: (message: string) => Promise<void> }) {
  const [windows, setWindows] = useState(schedule);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function update(index: number, patch: Partial<AvailabilityWindow>) {
    setWindows((current) => current.map((window, itemIndex) => itemIndex === index ? { ...window, ...patch } : window));
  }

  return (
    <section className="admin-panel">
      <div className="panel-head"><div><h2>Wöchentliche Verfügbarkeit</h2><p>Aus jedem Fenster entstehen 45-Minuten-Termine mit 15 Minuten Pause.</p></div><button className="button button-secondary button-small" type="button" onClick={() => setWindows((current) => [...current, { weekday: 1, startTime: "15:00", endTime: "19:00", isActive: true }])}>+ Zeitfenster</button></div>
      <div className="schedule-list">
        {windows.length === 0 && <div className="empty-state">Noch kein Wochenplan angelegt. Ohne Zeitfenster werden öffentlich keine Termine angezeigt.</div>}
        {windows.map((window, index) => (
          <div className="schedule-row" key={`${window.id ?? "new"}-${index}`}>
            <select aria-label="Wochentag" value={window.weekday} onChange={(event) => update(index, { weekday: Number(event.target.value) })}>{weekdays.map((day, value) => <option value={value} key={day}>{day}</option>)}</select>
            <input aria-label="Startzeit" type="time" value={window.startTime.slice(0, 5)} onChange={(event) => update(index, { startTime: event.target.value })} />
            <span>bis</span>
            <input aria-label="Endzeit" type="time" value={window.endTime.slice(0, 5)} onChange={(event) => update(index, { endTime: event.target.value })} />
            <label className="switch"><input type="checkbox" checked={window.isActive} onChange={(event) => update(index, { isActive: event.target.checked })} /><span /> aktiv</label>
            <button className="icon-button danger" type="button" aria-label="Zeitfenster löschen" onClick={() => setWindows((current) => current.filter((_, itemIndex) => itemIndex !== index))}><Trash2 /></button>
          </div>
        ))}
      </div>
      {error && <div className="alert alert-error" role="alert"><AlertCircle /> {error}</div>}
      <button className="button button-primary" type="button" disabled={busy} onClick={async () => {
        setBusy(true);
        setError("");
        try {
          await api.adminAction(session, "saveSchedule", { windows });
          await onSaved("Wochenplan gespeichert.");
        } catch (caught) {
          setError(caught instanceof Error ? caught.message : "Der Wochenplan konnte nicht gespeichert werden.");
        } finally {
          setBusy(false);
        }
      }}><Save /> {busy ? "Wird gespeichert …" : "Wochenplan speichern"}</button>
    </section>
  );
}

export function AdminApp() {
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [overview, setOverview] = useState<AdminOverview>();
  const [loading, setLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [tab, setTab] = useState<AdminTab>(() => adminTabFromSearch(window.location.search));
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [googleNotice, setGoogleNotice] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("google") === "connected") return { success: true, text: "Google-Konto wurde verbunden." };
    if (params.get("google") === "error") return { success: false, text: params.get("reason") === "google_account_mismatch"
      ? "Bitte verbinde das für diese Umgebung vorgesehene Google-Konto."
      : "Die Google-Verbindung ist fehlgeschlagen. Bitte versuche es erneut." };
    return null;
  });
  const notificationsSupported = "Notification" in window && "serviceWorker" in navigator;
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>(
    () => notificationsSupported ? Notification.permission : "denied",
  );
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    () => notificationsSupported
      && Notification.permission === "granted"
      && window.localStorage.getItem(ADMIN_NOTIFICATION_PREFERENCE_KEY) === "enabled",
  );
  const [realtimeNotificationStatus, setRealtimeNotificationStatus] = useState<RealtimeNotificationStatus>("disabled");

  useEffect(() => {
    if (!supabase) { setAuthLoading(false); return; }
    void supabase.auth.getSession().then(({ data }) => { setSession(data.session); setAuthLoading(false); });
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => { setSession(nextSession); setAuthLoading(false); });
    return () => data.subscription.unsubscribe();
  }, []);

  const loadOverview = useCallback(async (silent = false) => {
    if (!session) return;
    if (!silent) setLoading(true);
    setError("");
    try { setOverview(await api.adminOverview(session)); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Admin-Daten konnten nicht geladen werden."); }
    finally { if (!silent) setLoading(false); }
  }, [session]);

  useEffect(() => { void loadOverview(); }, [loadOverview]);

  useEffect(() => {
    if (!session || !supabase || !notificationsEnabled || notificationPermission !== "granted") {
      setRealtimeNotificationStatus("disabled");
      return;
    }

    const realtimeClient = supabase;
    let active = true;
    setRealtimeNotificationStatus("connecting");
    void navigator.serviceWorker.register(ADMIN_NOTIFICATION_SERVICE_WORKER, {
      scope: "/",
      updateViaCache: "none",
    });
    void realtimeClient.realtime.setAuth(session.access_token);

    const handleBroadcast = async (eventName: string, payload: unknown) => {
      const notification = parseAdminBroadcast(eventName, payload);
      if (!notification || !active) return;
      const copy = adminNotificationCopy(notification.kind);
      setMessage(copy.message);
      window.setTimeout(() => setMessage(""), 5000);
      await loadOverview(true);

      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.showNotification(copy.title, {
          body: copy.body,
          tag: `admin-${notification.kind}-${notification.id}`,
          data: { url: adminNotificationUrl(notification.kind, window.location.origin) },
        });
      } catch {
        setError("Die Desktop-Benachrichtigung konnte nicht angezeigt werden.");
      }
    };

    const channel = realtimeClient.channel(ADMIN_NOTIFICATION_CHANNEL, {
      config: { private: true },
    });
    channel
      .on("broadcast", { event: "booking-created" }, (payload) => void handleBroadcast("booking-created", payload))
      .on("broadcast", { event: "challenge-created" }, (payload) => void handleBroadcast("challenge-created", payload))
      .subscribe((status) => {
        if (!active) return;
        if (status === "SUBSCRIBED") setRealtimeNotificationStatus("connected");
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") setRealtimeNotificationStatus("error");
      });

    return () => {
      active = false;
      setRealtimeNotificationStatus("disabled");
      void realtimeClient.removeChannel(channel);
    };
  }, [loadOverview, notificationPermission, notificationsEnabled, session]);

  async function enableDesktopNotifications() {
    if (!notificationsSupported) {
      setError("Dieser Browser unterstützt keine Desktop-Benachrichtigungen.");
      return;
    }
    const permission = await Notification.requestPermission();
    setNotificationPermission(permission);
    if (permission !== "granted") {
      setNotificationsEnabled(false);
      window.localStorage.removeItem(ADMIN_NOTIFICATION_PREFERENCE_KEY);
      setError("Benachrichtigungen wurden im Browser nicht erlaubt.");
      return;
    }
    await navigator.serviceWorker.register(ADMIN_NOTIFICATION_SERVICE_WORKER, {
      scope: "/",
      updateViaCache: "none",
    });
    window.localStorage.setItem(ADMIN_NOTIFICATION_PREFERENCE_KEY, "enabled");
    setNotificationsEnabled(true);
    setError("");
    setMessage("Desktop-Benachrichtigungen wurden aktiviert.");
  }

  function disableDesktopNotifications() {
    window.localStorage.removeItem(ADMIN_NOTIFICATION_PREFERENCE_KEY);
    setNotificationsEnabled(false);
    setMessage("Desktop-Benachrichtigungen wurden deaktiviert.");
  }

  async function showAndReload(nextMessage: string) {
    setMessage(nextMessage);
    await loadOverview();
    window.setTimeout(() => setMessage(""), 5000);
  }

  async function action(actionName: string, data: Record<string, unknown>) {
    if (!session) return;
    if (actionName === "approveBooking" && !window.confirm("Diesen Bewerber verbindlich bestätigen und den Google-Termin erstellen?")) return;
    setActionBusy(true); setError("");
    try {
      await api.adminAction(session, actionName, data);
      const actionMessage = actionName === "approveBooking"
        ? "Termin wurde bestätigt und in Google Calendar angelegt."
        : actionName === "cancelBooking"
          ? "Termin wurde storniert, aus Google Calendar entfernt und wieder freigegeben."
          : actionName === "setChallengeEnabled"
            ? data.enabled ? "Challenge-Funktion wurde aktiviert." : "Challenge-Funktion wurde deaktiviert."
          : "Anfrage wurde aktualisiert.";
      await showAndReload(actionMessage);
    }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Aktion fehlgeschlagen."); }
    finally { setActionBusy(false); }
  }

  if (authLoading) return <div className="app-loading"><LoaderCircle className="spin" /> Anmeldung wird geprüft …</div>;
  if (!session) return <LoginPanel />;

  const pendingCount = overview?.bookings.filter((booking) => booking.status === "pending").length ?? 0;
  const newChallenges = overview?.challenges.filter((challenge) => challenge.status === "new").length ?? 0;

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <a className="brand admin-brand" href={viewUrl("landing")}><span className="brand-mark">J</span><span><strong>Mathe mit Joe</strong><small>Verwaltung</small></span></a>
        <nav>
          <button className={tab === "bookings" ? "active" : ""} onClick={() => setTab("bookings")}><CalendarClock /> Terminanfragen {pendingCount > 0 && <b>{pendingCount}</b>}</button>
          <button className={tab === "challenges" ? "active" : ""} onClick={() => setTab("challenges")}><Inbox /> Challenges {newChallenges > 0 && <b>{newChallenges}</b>}</button>
          <button className={tab === "settings" ? "active" : ""} onClick={() => setTab("settings")}><Settings /> Einstellungen</button>
        </nav>
        <div className="sidebar-bottom"><a href={viewUrl("landing")}><ArrowLeft /> Website ansehen</a><button type="button" onClick={() => void supabase?.auth.signOut()}><LogOut /> Abmelden</button></div>
      </aside>

      <main className="admin-main">
        <header className="admin-topbar"><div><p>Willkommen zurück</p><h1>{tab === "bookings" ? "Terminanfragen" : tab === "challenges" ? "Challenge-Pool" : "Einstellungen"}</h1></div><div className="admin-user"><CircleUserRound /><span>{session.user.email}</span></div></header>
        {googleNotice && <div className={googleNotice.success ? "alert alert-success" : "alert alert-error"}>{googleNotice.success ? <CheckCircle2 /> : <AlertCircle />} {googleNotice.text}</div>}
        {message && <div className="alert alert-success"><CheckCircle2 /> {message}</div>}
        {error && <div className="alert alert-error"><AlertCircle /> {error}</div>}
        {loading && <div className="loading-state"><LoaderCircle className="spin" /> Daten werden geladen …</div>}

        {!loading && overview && tab === "bookings" && (
          <section className="admin-panel">
            <div className="panel-head"><div><h2>Offene und aktuelle Anfragen</h2><p>Erst beim Bestätigen wird der Slot erneut geprüft und im Kalender blockiert.</p></div><button className="icon-button" type="button" onClick={() => void loadOverview()} aria-label="Aktualisieren"><RefreshCw /></button></div>
            <div className="admin-list">{overview.bookings.length ? overview.bookings.map((booking) => <BookingAdminCard booking={booking} busy={actionBusy} onAction={action} key={booking.id} />) : <div className="empty-state">Noch keine Terminanfragen vorhanden.</div>}</div>
          </section>
        )}

        {!loading && overview && tab === "challenges" && (
          <section className="admin-panel">
            <div className="panel-head"><div><h2>Eingesendete Mathe-Aufgaben</h2><p>Private Originale und veröffentlichte Inhalte bleiben strikt getrennt.</p></div></div>
            <div className="admin-list">{overview.challenges.length ? overview.challenges.map((challenge) => <ChallengeAdminCard challenge={challenge} session={session} onSaved={showAndReload} key={challenge.id} />) : <div className="empty-state">Noch keine Challenges vorhanden.</div>}</div>
          </section>
        )}

        {!loading && overview && tab === "settings" && (
          <div className="settings-stack">
            <section className="admin-panel integration-card">
              <div>
                <span className={overview.challengeEnabled ? "integration-icon connected" : "integration-icon"}><Zap /></span>
                <div>
                  <h2>Challenge-Funktion</h2>
                  <p>{overview.challengeEnabled ? "Uploads und Challenge-Galerie sind auf der Landingpage sichtbar." : "Die gesamte öffentliche Challenge-Funktion ist deaktiviert."}</p>
                </div>
              </div>
              <button
                className={overview.challengeEnabled ? "button button-danger" : "button button-primary"}
                type="button"
                disabled={actionBusy}
                onClick={() => {
                  if (overview.challengeEnabled && !window.confirm("Challenge-Uploads und Galerie auf der Landingpage wirklich deaktivieren?")) return;
                  void action("setChallengeEnabled", { enabled: !overview.challengeEnabled });
                }}
              >
                <Zap /> {overview.challengeEnabled ? "Deaktivieren" : "Aktivieren"}
              </button>
            </section>
            <section className="admin-panel integration-card notification-card">
              <div>
                <span className={realtimeNotificationStatus === "connected" ? "integration-icon connected" : "integration-icon"}>
                  {notificationsEnabled ? <Bell /> : <BellOff />}
                </span>
                <div>
                  <h2>Desktop-Benachrichtigungen</h2>
                  <p>
                    {!notificationsSupported
                      ? "Dieser Browser unterstützt die Funktion nicht."
                      : notificationPermission === "denied"
                        ? "Im Browser blockiert. Bitte in den Website-Einstellungen erlauben."
                        : realtimeNotificationStatus === "connected"
                          ? "Aktiv und mit neuen Anfragen verbunden."
                          : realtimeNotificationStatus === "connecting"
                            ? "Wird mit neuen Anfragen verbunden …"
                            : realtimeNotificationStatus === "error"
                              ? "Verbindung fehlgeschlagen. Seite neu laden und erneut versuchen."
                              : "Meldet neue Termine und Challenges auch bei einem inaktiven Fenster."}
                  </p>
                </div>
              </div>
              {notificationsEnabled ? (
                <button className="button button-secondary" type="button" onClick={disableDesktopNotifications}>
                  <BellOff /> Deaktivieren
                </button>
              ) : (
                <button
                  className="button button-primary"
                  type="button"
                  disabled={!notificationsSupported || notificationPermission === "denied"}
                  onClick={() => void enableDesktopNotifications()}
                >
                  <Bell /> Aktivieren
                </button>
              )}
            </section>
            {!appConfig.stagingMode && <section className="admin-panel integration-card">
              <div><span className={overview.google.connected ? "integration-icon connected" : "integration-icon"}><Link2 /></span><div><h2>Google Calendar, Meet & Gmail</h2><p>{overview.google.connected ? `Verbunden mit ${overview.google.email}` : "Noch nicht mit deinem Google-Konto verbunden."}</p></div></div>
              <button className="button button-secondary" type="button" onClick={async () => {
                setGoogleNotice(null);
                setError("");
                try {
                  const result = await api.adminAction<{ url: string }>(session, "googleConnectUrl");
                  window.location.href = result.url;
                } catch (caught) {
                  setError(caught instanceof Error ? caught.message : "Die Google-Verbindung konnte nicht gestartet werden.");
                }
              }}><Link2 /> {overview.google.connected ? "Neu verbinden" : "Google verbinden"}</button>
            </section>}
            {appConfig.stagingMode && <section className="admin-panel integration-card"><div><span className="integration-icon connected"><CheckCircle2 /></span><div><h2>Sichere Testumgebung</h2><p>Termine und Statusänderungen bleiben in Staging. Es werden keine Google-Termine oder E-Mails erzeugt.</p></div></div></section>}
            <ScheduleEditor schedule={overview.schedule} session={session} onSaved={showAndReload} />
            <section className="admin-panel setup-notes"><h2>Livegang-Checkliste</h2><ul><li><CheckCircle2 /> Google-Konto verbunden und Testtermin durchgeführt</li><li><CheckCircle2 /> Wochenplan mit echten Zeiten gespeichert</li><li><CheckCircle2 /> Turnstile für beide öffentlichen Formulare aktiviert</li><li><CheckCircle2 /> Impressum und Datenschutz durch vollständige Angaben ersetzt</li><li><CheckCircle2 /> Cloudflare-Pages-URL als <code>PUBLIC_SITE_URL</code> gesetzt</li></ul></section>
          </div>
        )}
      </main>
    </div>
  );
}
